import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type { components } from "./schema";
import { api, sendFile, unwrap, unwrapEmpty, type UploadProgress } from "./client";
import { reloadThemeStylesheet } from "@/theme/stylesheet";

export type ThemeDocument = components["schemas"]["ThemeDocument"];
export type SessionStatus = components["schemas"]["SessionStatus"];
export type DeploymentMeta = components["schemas"]["DeploymentMeta"];
export type FixtureList = components["schemas"]["FixtureList"];
export type DemoFixture = components["schemas"]["DemoFixture"];

export type ConnectionSummary = components["schemas"]["ConnectionSummary"];
export type ConnectionDetail = components["schemas"]["ConnectionDetail"];
export type SourceFile = components["schemas"]["SourceFileOut"];
export type FileReplaced = components["schemas"]["FileReplaced"];
export type FileStorage = components["schemas"]["StorageOut"];
export type ConnectionWrite = components["schemas"]["ConnectionWrite"];
export type ConnectionPatch = components["schemas"]["ConnectionPatch"];
export type SourceTypeInfo = components["schemas"]["SourceTypeInfo"];
export type FieldInfo = components["schemas"]["FieldInfo"];
export type Diagnostics = components["schemas"]["DiagnosticsOut"];
export type DiagnosticStep = components["schemas"]["DiagnosticStepOut"];
export type ConnectionSchema = components["schemas"]["SchemaOut"];
export type Relation = components["schemas"]["RelationOut"];
export type RelationDefinition = components["schemas"]["RelationDefinitionOut"];
export type Column = components["schemas"]["ColumnOut"];
export type Knowledge = components["schemas"]["KnowledgeOut"];
export type FileImport = components["schemas"]["ImportOut"];
export type ImportResult = components["schemas"]["ImportResult"];
export type QueryResult = components["schemas"]["QueryResultOut"];

/**
 * "The surface that calls this mutation renders its own failure."
 *
 * Every mutation in this file is announced by `MutationCache.onError`
 * (`main.tsx`) unless it says otherwise here — the default is loud, and
 * silence is the thing that has to be asked for. That inversion is backlog
 * item 11: the 204 delete bug survived because a failure with no surface is
 * indistinguishable from a no-op, and the next mutation somebody adds gets the
 * safety net without having to remember it exists.
 *
 * Marked here on the mutation rather than at the call site because the error
 * surface is a property of the *operation* — saving the prompt belongs to the
 * prompt editor — and because a per-call-site convention is exactly the kind
 * that decays. Each one below names the surface that owns it, so a second call
 * site with no surface of its own is visible in review.
 */
const INLINE = { errorSurface: "inline" } as const;

export const themeQuery = queryOptions({
  queryKey: ["theme"],
  queryFn: async () => unwrap(await api.GET("/api/theme")),
  staleTime: 60_000,
});

export const sessionQuery = queryOptions({
  queryKey: ["session"],
  queryFn: async () => unwrap(await api.GET("/api/auth/session")),
  staleTime: 30_000,
  retry: false,
});

export const metaQuery = queryOptions({
  queryKey: ["meta"],
  queryFn: async () => unwrap(await api.GET("/api/meta")),
  staleTime: 300_000,
});

export const fixturesQuery = queryOptions({
  queryKey: ["demo", "fixtures"],
  queryFn: async () => unwrap(await api.GET("/api/demo/fixtures")),
  retry: false,
});

export function useTheme() {
  return useQuery(themeQuery);
}

export function useSession() {
  return useQuery(sessionQuery);
}

/**
 * Whether this session is the Admin tier (ACC-2).
 *
 * For *hiding* what an analyst cannot use — never for enforcing it. The API
 * refuses those operations to an analyst whatever this says, and the role
 * sweep in `test_roles.py` is what proves it; hiding a button is a courtesy.
 */
export function useIsAdmin() {
  const { data } = useSession();
  return data?.role === "admin";
}

export function useMeta() {
  return useQuery(metaQuery);
}

export function useFixtures(enabled: boolean) {
  return useQuery({ ...fixturesQuery, enabled });
}

export function useUnlock() {
  const queryClient = useQueryClient();
  return useMutation({
    // UnlockPage — the error is the whole interaction.
    meta: INLINE,
    // A key, or a name and password (ACC-3) — the server takes exactly one.
    mutationFn: async (
      credentials: { access_key: string } | { username: string; password: string },
    ) => unwrap(await api.POST("/api/auth/unlock", { body: credentials })),
    onSuccess: async (session) => {
      // Write the new status straight into the cache: the router's beforeLoad
      // reads it synchronously, so waiting on a background refetch would let it
      // see the pre-unlock value and bounce straight back to /unlock.
      queryClient.setQueryData(sessionQuery.queryKey, session);
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== sessionQuery.queryKey[0],
      });
    },
  });
}

export function useLock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/auth/lock")),
    onSuccess: (session) => {
      // Drop everything the session could have populated, then record the
      // locked status so the next navigation guard sees it immediately.
      queryClient.clear();
      queryClient.setQueryData(sessionQuery.queryKey, session);
    },
  });
}


// ---------------------------------------------------------------------------
// Connections (PRD FR-1, FR-2)
// ---------------------------------------------------------------------------

export const connectionsQuery = queryOptions({
  queryKey: ["connections"],
  queryFn: async () => unwrap(await api.GET("/api/connections")),
});

export const sourceTypesQuery = queryOptions({
  queryKey: ["connections", "source-types"],
  queryFn: async () => unwrap(await api.GET("/api/connections/source-types")),
  staleTime: Infinity,
});

export function connectionQuery(id: string) {
  return queryOptions({
    queryKey: ["connections", id],
    queryFn: async () =>
      unwrap(await api.GET("/api/connections/{connection_id}", { params: { path: { connection_id: id } } })),
  });
}

export function connectionSchemaQuery(id: string) {
  return queryOptions({
    queryKey: ["connections", id, "schema"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/connections/{connection_id}/schema", {
          params: { path: { connection_id: id }, query: { include_columns: true } },
        }),
      ),
    // Introspection can take seconds on a wide schema, so the browser reads the
    // server-side cache rather than re-fetching on every focus change.
    staleTime: 5 * 60_000,
  });
}

export function knowledgeQuery(id: string) {
  return queryOptions({
    queryKey: ["connections", id, "knowledge"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/connections/{connection_id}/knowledge", {
          params: { path: { connection_id: id } },
        }),
      ),
  });
}

export function importsQuery(id: string, enabled: boolean) {
  return {
    ...queryOptions({
      queryKey: ["connections", id, "imports"],
      queryFn: async () =>
        unwrap(
          await api.GET("/api/connections/{connection_id}/imports", {
            params: { path: { connection_id: id } },
          }),
        ),
    }),
    enabled,
  };
}

export function useConnections() {
  return useQuery(connectionsQuery);
}

export function useSourceTypes() {
  return useQuery(sourceTypesQuery);
}

export function useConnection(id: string) {
  return useQuery(connectionQuery(id));
}

export function useConnectionSchema(id: string) {
  return useQuery(connectionSchemaQuery(id));
}

export function useKnowledge(id: string) {
  return useQuery(knowledgeQuery(id));
}

export function useImports(id: string, enabled: boolean) {
  return useQuery(importsQuery(id, enabled));
}

export function useCreateConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    // SourcesPage — under the new-source form.
    meta: INLINE,
    mutationFn: async (body: ConnectionWrite) =>
      unwrap(await api.POST("/api/connections", { body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connections"] }),
  });
}

/**
 * Make a source by uploading a file (FILE-2). Inline: the drop zone shows the
 * refusal under itself, naming the limit, and keeps the file chosen.
 */
export function useUploadSource() {
  const queryClient = useQueryClient();
  return useMutation({
    // SourcesPage — under the upload drop zone.
    meta: INLINE,
    mutationFn: ({
      file,
      name,
      onProgress,
    }: {
      file: File;
      name?: string;
      onProgress?: UploadProgress;
    }) =>
      sendFile<ConnectionDetail>(
        "POST",
        "/api/connections/upload",
        file,
        { filename: file.name, name: name?.trim() || undefined },
        onProgress,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
      void queryClient.invalidateQueries({ queryKey: ["files", "storage"] });
    },
  });
}

export function useReplaceSourceFile(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // SourceFileCard — under the replace control.
    meta: INLINE,
    mutationFn: ({ file, onProgress }: { file: File; onProgress?: UploadProgress }) =>
      sendFile<FileReplaced>(
        "PUT",
        `/api/connections/${encodeURIComponent(id)}/file`,
        file,
        { filename: file.name },
        onProgress,
      ),
    onSuccess: () => {
      // The schema moved with the file; everything under this source is stale.
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
      void queryClient.invalidateQueries({ queryKey: ["files", "storage"] });
    },
  });
}

/** One tile as a self-contained HTML file (HTML-1). */
export function tileHtmlUrl(id: string): string {
  return `/api/tiles/${encodeURIComponent(id)}/export.html`;
}

export function sourceFileUrl(id: string): string {
  return `/api/connections/${encodeURIComponent(id)}/file`;
}

export function useFileStorage(enabled = true) {
  return useQuery({
    queryKey: ["files", "storage"],
    enabled,
    queryFn: async () => unwrap(await api.GET("/api/files/storage")),
  });
}

export function useUpdateConnection(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // SourceDetailPage — under the settings form.
    meta: INLINE,
    mutationFn: async (body: ConnectionPatch) =>
      unwrap(
        await api.PATCH("/api/connections/{connection_id}", {
          params: { path: { connection_id: id } },
          body,
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connections"] }),
  });
}

export function useDeleteConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/connections/{connection_id}", {
          params: { path: { connection_id: id } },
        }),
      ),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ["connections", id] });
      void queryClient.invalidateQueries({ queryKey: ["connections"], exact: true });
      // Its declared relationships went with it (MS-1).
      void queryClient.invalidateQueries({ queryKey: ["relationships"] });
    },
  });
}

export function useTestConnection(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/connections/{connection_id}/test", {
          params: { path: { connection_id: id } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connections"] }),
  });
}

/** Validate parameters before anything is stored (PRD FR-1.2). */
export function useTestDraft() {
  return useMutation({
    // SourcesPage — beside the Test button, where the verdict goes.
    meta: INLINE,
    mutationFn: async (body: ConnectionWrite) =>
      unwrap(await api.POST("/api/connections/test", { body })),
  });
}

export function useRefreshSchema(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/connections/{connection_id}/schema/refresh", {
          params: { path: { connection_id: id }, query: { include_columns: true } },
        }),
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(connectionSchemaQuery(id).queryKey, result);
      void queryClient.invalidateQueries({ queryKey: ["connections", id] });
      // A refreshed schema can make a declaration stale, or fresh again.
      void queryClient.invalidateQueries({ queryKey: ["relationships"] });
    },
  });
}

export function useSaveKnowledge(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["KnowledgeUpdate"]) =>
      unwrap(
        await api.PUT("/api/connections/{connection_id}/knowledge", {
          params: { path: { connection_id: id } },
          body,
        }),
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(knowledgeQuery(id).queryKey, result);
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });
}

export function useSeedDemoConnections() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.POST("/api/demo/seed");
      return unwrap(await api.POST("/api/demo/connections"));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["connections"] }),
  });
}


// ---------------------------------------------------------------------------
// Declared relationships between sources (PRD FR-1.9, MS-1)
// ---------------------------------------------------------------------------

export type Relationship = components["schemas"]["RelationshipOut"];
export type RelationshipEnd = components["schemas"]["RelationshipEnd"];
export type RelationshipWrite = components["schemas"]["RelationshipWrite"];
export type RelationshipSuggestion = components["schemas"]["RelationshipSuggestionOut"];
export type RelationshipOverlap = components["schemas"]["RelationshipOverlapOut"];

/** Declarations touching `connectionId`, or every one when it is omitted. */
export function useRelationships(connectionId?: string) {
  return useQuery({
    queryKey: ["relationships", connectionId ?? "all"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/relationships", {
          params: { query: connectionId ? { connection_id: connectionId } : {} },
        }),
      ),
  });
}

export function useRelationshipSuggestions(left: string, right: string, enabled: boolean) {
  return useQuery({
    queryKey: ["relationships", "suggestions", left, right],
    queryFn: async () =>
      unwrap(await api.GET("/api/relationships/suggestions", { params: { query: { left, right } } })),
    enabled: enabled && Boolean(left && right),
  });
}

export function useCreateRelationship() {
  const queryClient = useQueryClient();
  return useMutation({
    // RelationshipDialog — under the form, where the refusal names the column.
    meta: INLINE,
    mutationFn: async (body: RelationshipWrite) =>
      unwrap(await api.POST("/api/relationships", { body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["relationships"] }),
  });
}

export function useUpdateRelationship() {
  const queryClient = useQueryClient();
  return useMutation({
    // RelationshipDialog — under the form.
    meta: INLINE,
    mutationFn: async ({
      id,
      ...body
    }: { id: string } & components["schemas"]["RelationshipPatch"]) =>
      unwrap(
        await api.PATCH("/api/relationships/{relationship_id}", {
          params: { path: { relationship_id: id } },
          body,
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["relationships"] }),
  });
}

export function useDeleteRelationship() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/relationships/{relationship_id}", {
          params: { path: { relationship_id: id } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["relationships"] }),
  });
}

/** Two guarded `SELECT DISTINCT`s, one per side — audited, and never saved. */
export function useCheckRelationship() {
  return useMutation({
    // RelationshipDialog — the verdict renders beside the button.
    meta: INLINE,
    mutationFn: async (body: RelationshipWrite) =>
      unwrap(await api.POST("/api/relationships/check", { body })),
  });
}

// ---------------------------------------------------------------------------
// Model providers (PRD FR-3)
// ---------------------------------------------------------------------------

export type ProviderSummary = components["schemas"]["ProviderSummary"];
export type ProviderKindInfo = components["schemas"]["ProviderKindInfo"];
export type ProviderWrite = components["schemas"]["ProviderWrite"];
export type ProviderPatch = components["schemas"]["ProviderPatch"];
export type ProviderHealth = components["schemas"]["ProviderHealth"];
export type ModelOut = components["schemas"]["ModelOut"];

export const providersQuery = queryOptions({
  queryKey: ["providers"],
  queryFn: async () => unwrap(await api.GET("/api/providers")),
});

export const providerKindsQuery = queryOptions({
  queryKey: ["providers", "kinds"],
  queryFn: async () => unwrap(await api.GET("/api/providers/kinds")),
  staleTime: Infinity,
});

export const modelsQuery = queryOptions({
  queryKey: ["models"],
  queryFn: async () => unwrap(await api.GET("/api/models")),
});

export function useProviders() {
  return useQuery(providersQuery);
}

export function useProviderKinds() {
  return useQuery(providerKindsQuery);
}

export function useModels() {
  return useQuery(modelsQuery);
}

/** Everything a provider write touches invalidates the catalog too. */
function invalidateProviders(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["providers"] });
  void queryClient.invalidateQueries({ queryKey: ["models"] });
}

export function useCreateProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    // ProvidersPage — inside the add-provider form.
    meta: INLINE,
    mutationFn: async (body: ProviderWrite) => unwrap(await api.POST("/api/providers", { body })),
    onSuccess: () => invalidateProviders(queryClient),
  });
}

export function useUpdateProvider(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ProviderPatch) =>
      unwrap(
        await api.PATCH("/api/providers/{provider_id}", {
          params: { path: { provider_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidateProviders(queryClient),
  });
}

export function useDeleteProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/providers/{provider_id}", {
          params: { path: { provider_id: id } },
        }),
      ),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ["providers", id] });
      invalidateProviders(queryClient);
    },
  });
}

export function useCheckProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.POST("/api/providers/{provider_id}/check", {
          params: { path: { provider_id: id } },
        }),
      ),
    onSuccess: () => invalidateProviders(queryClient),
  });
}


// ---------------------------------------------------------------------------
// The model catalog (PRD FR-3.3, FR-3.4, FR-3.5)
// ---------------------------------------------------------------------------

export type ProviderCatalog = components["schemas"]["ProviderCatalog"];
export type CatalogModelPatch = components["schemas"]["CatalogModelPatch"];
export type DefaultModel = components["schemas"]["DefaultModel"];
export type DefaultModelWrite = components["schemas"]["DefaultModelWrite"];

export function providerCatalogQuery(id: string) {
  return queryOptions({
    queryKey: ["providers", id, "catalog"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/providers/{provider_id}/catalog", {
          params: { path: { provider_id: id } },
        }),
      ),
  });
}

export const defaultModelQuery = queryOptions({
  queryKey: ["models", "default"],
  queryFn: async () => unwrap(await api.GET("/api/models/default")),
});

export function useProviderCatalog(id: string) {
  return useQuery(providerCatalogQuery(id));
}

export function useDefaultModel() {
  return useQuery(defaultModelQuery);
}

/** Hide, rename, re-window, re-price or re-prompt one model. */
export function useUpdateCatalogModel(providerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CatalogModelPatch) =>
      unwrap(
        await api.PATCH("/api/providers/{provider_id}/catalog", {
          params: { path: { provider_id: providerId } },
          body,
        }),
      ),
    onSuccess: () => invalidateProviders(queryClient),
  });
}

export function useAddCatalogModel(providerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) =>
      unwrap(
        await api.POST("/api/providers/{provider_id}/catalog", {
          params: { path: { provider_id: providerId } },
          body: { name },
        }),
      ),
    onSuccess: () => invalidateProviders(queryClient),
  });
}

export function useRemoveCatalogModel(providerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) =>
      unwrap(
        await api.POST("/api/providers/{provider_id}/catalog/remove", {
          params: { path: { provider_id: providerId } },
          body: { name },
        }),
      ),
    onSuccess: () => invalidateProviders(queryClient),
  });
}

export function useSetDefaultModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ref: string | null) =>
      unwrap(await api.PUT("/api/models/default", { body: { ref } })),
    onSuccess: () => invalidateProviders(queryClient),
  });
}


// ---------------------------------------------------------------------------
// Usage and cost (PRD FR-3.5)
// ---------------------------------------------------------------------------

export type UsageSummary = components["schemas"]["UsageSummaryOut"];
export type UsageBucket = components["schemas"]["UsageBucket"];
export type UsageEvent = components["schemas"]["UsageEventOut"];

export function usageQuery(days: number) {
  return queryOptions({
    queryKey: ["usage", { days }],
    queryFn: async () => unwrap(await api.GET("/api/usage", { params: { query: { days } } })),
  });
}

export function usageEventsQuery(limit: number) {
  return queryOptions({
    queryKey: ["usage", "events", { limit }],
    queryFn: async () =>
      unwrap(await api.GET("/api/usage/events", { params: { query: { limit } } })),
  });
}

export function useUsage(days: number) {
  return useQuery(usageQuery(days));
}

export function useUsageEvents(limit = 25) {
  return useQuery(usageEventsQuery(limit));
}


// ---------------------------------------------------------------------------
// Conversations (PRD FR-4)
// ---------------------------------------------------------------------------

export type ThreadSummary = components["schemas"]["ThreadSummary"];
export type ThreadDetail = components["schemas"]["ThreadDetail"];
export type ChatMessage = components["schemas"]["MessageOut"];
export type QueryStep = components["schemas"]["QueryStepOut"];
export type AgentRun = components["schemas"]["RunOut"];
export type StartedRun = components["schemas"]["StartedRunOut"];
export type Landing = components["schemas"]["LandingOut"];

export function threadsQuery(search: string) {
  return queryOptions({
    queryKey: ["threads", { search }],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/threads", {
          params: { query: search ? { search } : {} },
        }),
      ),
  });
}

export function threadQuery(id: string) {
  return queryOptions({
    queryKey: ["threads", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/threads/{thread_id}", { params: { path: { thread_id: id } } }),
      ),
    // `/chat` has no thread, and asks for one anyway: ChatPage passes
    // `threadId ?? ""`, which fetched `/api/threads/` and logged a 404 on every
    // visit to the conversation index. Noise in the place you go to look for
    // real failures.
    enabled: Boolean(id),
  });
}

export function landingQuery(id: string, enabled: boolean) {
  return {
    ...queryOptions({
      queryKey: ["threads", id, "landing"],
      queryFn: async () =>
        unwrap(
          await api.GET("/api/threads/{thread_id}/landing", {
            params: { path: { thread_id: id } },
          }),
        ),
      staleTime: 5 * 60_000,
    }),
    enabled,
  };
}

export function useThreads(search: string) {
  return useQuery(threadsQuery(search));
}

export function useThread(id: string) {
  return useQuery(threadQuery(id));
}

export function useLanding(id: string, enabled: boolean) {
  return useQuery(landingQuery(id, enabled));
}

export function useCreateThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["ThreadCreate"]) =>
      unwrap(await api.POST("/api/threads", { body })),
    onSuccess: (thread) => {
      queryClient.setQueryData(threadQuery(thread.id).queryKey, thread);
      // The *list*, not every thread detail under it. Creating a conversation
      // discards the blank it replaces (FR-4.8), and a blanket invalidate
      // refetches that dead thread — the browser is still on its route for the
      // moment it takes to navigate — turning a working button into a 404 in
      // the network log. `["threads", {search}]` is the list; `["threads", id]`
      // is a detail, and no detail but this one can have changed.
      void queryClient.invalidateQueries({
        queryKey: ["threads"],
        predicate: (query) => typeof query.queryKey[1] === "object",
      });
    },
  });
}

export function useUpdateThread(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["ThreadPatch"]) =>
      unwrap(
        await api.PATCH("/api/threads/{thread_id}", {
          params: { path: { thread_id: id } },
          body,
        }),
      ),
    onSuccess: (thread) => {
      queryClient.setQueryData(threadQuery(id).queryKey, thread);
      void queryClient.invalidateQueries({ queryKey: ["threads"] });
    },
  });
}

export function useDeleteThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/threads/{thread_id}", {
          params: { path: { thread_id: id } },
        }),
      ),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ["threads", id] });
      void queryClient.invalidateQueries({ queryKey: ["threads"] });
    },
  });
}

export function useSendMessage(threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // ChatPage — the composer keeps the question and says why.
    meta: INLINE,
    mutationFn: async (body: { text: string; model_ref?: string | null }) =>
      unwrap(
        await api.POST("/api/threads/{thread_id}/messages", {
          params: { path: { thread_id: threadId } },
          body,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["threads", threadId] });
      void queryClient.invalidateQueries({ queryKey: ["threads"], exact: true });
    },
  });
}

export function useRegenerate(threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { model_ref?: string | null }) =>
      unwrap(
        await api.POST("/api/threads/{thread_id}/regenerate", {
          params: { path: { thread_id: threadId } },
          body,
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["threads", threadId] }),
  });
}

export type ThreadContext = components["schemas"]["ContextOut"];
export type Compaction = components["schemas"]["CompactionOut"];

/** The composer's context meter (PRD FR-3.4).
 *
 *  `draft` is the question being typed, so the meter moves while a long
 *  question is written rather than only once it is sent. Debouncing is the
 *  caller's job — this is a plain query keyed on what it measured. */
export function threadContextQuery(id: string, draft: string, modelRef: string) {
  return queryOptions({
    queryKey: ["threads", id, "context", { draft, modelRef }],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/threads/{thread_id}/context", {
          params: {
            path: { thread_id: id },
            query: { draft, ...(modelRef ? { model_ref: modelRef } : {}) },
          },
        }),
      ),
    // The prompt only changes when a turn lands or the draft does, both of
    // which change the key.
    staleTime: 30_000,
  });
}

export function useThreadContext(id: string, draft: string, modelRef: string, enabled: boolean) {
  return useQuery({ ...threadContextQuery(id, draft, modelRef), enabled });
}

/** Fold older turns into the pinned briefing by hand (PRD FR-3.4). */
export function useCompactThread(threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { model_ref?: string | null; keep_turns?: number | null }) =>
      unwrap(
        await api.POST("/api/threads/{thread_id}/compact", {
          params: { path: { thread_id: threadId } },
          body,
        }),
      ),
    onSuccess: (thread) => {
      queryClient.setQueryData(threadQuery(threadId).queryKey, thread);
      void queryClient.invalidateQueries({ queryKey: ["threads", threadId, "context"] });
    },
  });
}

export function useStopRun() {
  return useMutation({
    mutationFn: async (runId: string) =>
      unwrap(await api.POST("/api/runs/{run_id}/stop", { params: { path: { run_id: runId } } })),
  });
}

export function useRateMessage(threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ messageId, value }: { messageId: string; value: number }) =>
      unwrap(
        await api.POST("/api/messages/{message_id}/feedback", {
          params: { path: { message_id: messageId } },
          body: { value },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["threads", threadId] }),
  });
}


// ---------------------------------------------------------------------------
// Artifacts (PRD FR-6)
// ---------------------------------------------------------------------------

export type ArtifactKindCatalog = components["schemas"]["ArtifactKindsOut"];

/** The vocabulary this deployment renders. Published so the swap menu is
 *  data rather than a list hardcoded twice (PRD FR-6.1). */
export const artifactKindsQuery = queryOptions({
  queryKey: ["artifacts", "kinds"],
  queryFn: async () => unwrap(await api.GET("/api/artifacts/kinds")),
  staleTime: Infinity,
});

export function useArtifactKinds() {
  return useQuery(artifactKindsQuery);
}

/** A TopoJSON topology, as `GET /api/geo/{set}` returns it (MAP-1). */
export interface GeoTopology {
  type: "Topology";
  objects: Record<string, unknown>;
  arcs: unknown;
  transform?: unknown;
  bbox?: number[];
}

/**
 * One boundary set's geometry, keyed by its content version (MAP-1).
 *
 * A map artifact names the set *and* the version, so this is one request, and
 * the URL changes whenever the build's boundaries do — which is what lets the
 * server mark it cacheable for a year. The HTML export and the embed seed this
 * key from their payload instead, because neither can call back.
 */
export function geoQuery(set: string, version: string) {
  return queryOptions({
    queryKey: ["geo", set, version],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/geo/{set_key}", {
          params: { path: { set_key: set }, query: { v: version } },
        }),
      ) as unknown as GeoTopology,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** Swap a chart kind and persist it onto the message (PRD FR-6.4). */
export function useSwapArtifact(threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // ArtifactCard — the refusal is rendered under the card it belongs to.
    meta: INLINE,
    mutationFn: async ({
      messageId,
      index,
      type,
    }: {
      messageId: string;
      index: number;
      type: string;
    }) =>
      unwrap(
        await api.PATCH("/api/messages/{message_id}/artifacts/{index}", {
          params: { path: { message_id: messageId, index } },
          body: { type },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["threads", threadId] }),
  });
}

/** A selection as the view routes take it (LINK-3). */
export interface ArtifactSelection {
  link: string;
  grain: string;
  keys: string[];
}

/**
 * One of an answer's artifacts narrowed to a linked selection (LINK-3).
 *
 * A query rather than a mutation: nothing is written, and going back to a
 * selection already seen should be instant. Keyed on the artifact's kind too,
 * because a swap changes what the narrowed chart is drawn as.
 */
export function messageArtifactView(
  messageId: string,
  index: number,
  kind: string,
  selection: ArtifactSelection,
) {
  return queryOptions({
    queryKey: [
      "artifact-view",
      "message",
      messageId,
      index,
      kind,
      selection.link,
      selection.grain,
      [...selection.keys].sort().join("\u0000"),
    ],
    queryFn: async () =>
      unwrap(
        await api.POST("/api/messages/{message_id}/artifacts/{index}/view", {
          params: { path: { message_id: messageId, index } },
          body: { link: selection.link, grain: selection.grain, keys: selection.keys },
        }),
      ),
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * One of an answer's artifacts fetched again for a selection (LINK-4).
 *
 * Only for an artifact whose rows were cut off by the connection's cap: the
 * server re-runs its query scoped to the selection, guarded and audited. A
 * query rather than a mutation so the same selection is fetched once per
 * visit, not once per click.
 */
export function messageArtifactDrill(
  messageId: string,
  index: number,
  kind: string,
  selection: ArtifactSelection,
) {
  return queryOptions({
    queryKey: [
      "artifact-drill",
      "message",
      messageId,
      index,
      kind,
      selection.link,
      selection.grain,
      [...selection.keys].sort().join("\u0000"),
    ],
    queryFn: async () =>
      unwrap(
        await api.POST("/api/messages/{message_id}/artifacts/{index}/drill", {
          params: { path: { message_id: messageId, index } },
          body: { link: selection.link, grain: selection.grain, keys: selection.keys },
        }),
      ),
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * A shaded map drilled into one region (LINK-MAP): a state's counties, shaded
 * by the same measure. Cached per region, so going back up and down again
 * costs nothing — and a capped source is re-run once, not once per click.
 */
export function messageArtifactZoom(messageId: string, index: number, link: string, key: string) {
  return queryOptions({
    queryKey: ["artifact-zoom", "message", messageId, index, link, key],
    queryFn: async () =>
      unwrap(
        await api.POST("/api/messages/{message_id}/artifacts/{index}/zoom", {
          params: { path: { message_id: messageId, index } },
          body: { link, key },
        }),
      ),
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * A tile narrowed, fetched again or zoomed for its grid's selection (LINK-7).
 * The same three operations as an answer's artifacts, addressed by tile —
 * the server recomputes the grid's links from the tile's own report or
 * dashboard. `view` and `zoom` from stored rows are open to a share's reader;
 * `drill` is not.
 */
export function tileArtifactView(tileId: string, kind: string, selection: ArtifactSelection) {
  return queryOptions({
    queryKey: ["artifact-view", "tile", tileId, kind, ...selectionKey(selection)],
    queryFn: async () =>
      unwrap(
        await api.POST("/api/tiles/{tile_id}/view", {
          params: { path: { tile_id: tileId } },
          body: { link: selection.link, grain: selection.grain, keys: selection.keys },
        }),
      ),
    staleTime: Infinity,
    retry: false,
  });
}

export function tileArtifactDrill(tileId: string, kind: string, selection: ArtifactSelection) {
  return queryOptions({
    queryKey: ["artifact-drill", "tile", tileId, kind, ...selectionKey(selection)],
    queryFn: async () =>
      unwrap(
        await api.POST("/api/tiles/{tile_id}/drill", {
          params: { path: { tile_id: tileId } },
          body: { link: selection.link, grain: selection.grain, keys: selection.keys },
        }),
      ),
    staleTime: Infinity,
    retry: false,
  });
}

export function tileArtifactZoom(tileId: string, link: string, key: string) {
  return queryOptions({
    queryKey: ["artifact-zoom", "tile", tileId, link, key],
    queryFn: async () =>
      unwrap(
        await api.POST("/api/tiles/{tile_id}/zoom", {
          params: { path: { tile_id: tileId } },
          body: { link, key },
        }),
      ),
    staleTime: Infinity,
    retry: false,
  });
}

function selectionKey(selection: ArtifactSelection): string[] {
  return [selection.link, selection.grain, [...selection.keys].sort().join("\u0000")];
}

/** Where an artifact's CSV lives (PRD FR-6.6). A link, not a fetch: the
 *  browser's own download machinery handles the filename and the save. */
export function artifactCsvUrl(messageId: string, index: number): string {
  return `/api/messages/${encodeURIComponent(messageId)}/artifacts/${index}/export.csv`;
}


// ---------------------------------------------------------------------------
// Reports, tiles and dashboards (PRD FR-7)
// ---------------------------------------------------------------------------

export type ReportSummary = components["schemas"]["ReportSummary"];
export type ReportDetail = components["schemas"]["ReportDetail"];
export type DashboardSummary = components["schemas"]["DashboardSummary"];
export type DashboardDetail = components["schemas"]["DashboardDetail"];
export type Tile = components["schemas"]["TileOut"];
export type TileFrame = components["schemas"]["TileFrame"];
export type RefreshSummary = components["schemas"]["RefreshSummary"];
export type TileSqlResult = components["schemas"]["TileSqlResult"];

export function reportsQuery(search: string) {
  return queryOptions({
    queryKey: ["reports", { search }],
    queryFn: async () =>
      unwrap(await api.GET("/api/reports", { params: { query: search ? { search } : {} } })),
  });
}

export function reportQuery(id: string) {
  return queryOptions({
    queryKey: ["reports", id],
    queryFn: async () =>
      unwrap(await api.GET("/api/reports/{report_id}", { params: { path: { report_id: id } } })),
  });
}

export const dashboardsQuery = queryOptions({
  queryKey: ["dashboards"],
  queryFn: async () => unwrap(await api.GET("/api/dashboards")),
});

export function dashboardQuery(id: string) {
  return queryOptions({
    queryKey: ["dashboards", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/dashboards/{dashboard_id}", {
          params: { path: { dashboard_id: id } },
        }),
      ),
  });
}

export function useReports(search = "") {
  return useQuery(reportsQuery(search));
}

export function useReport(id: string) {
  return useQuery({ ...reportQuery(id), enabled: Boolean(id) });
}

export function useDashboards() {
  return useQuery(dashboardsQuery);
}

export function useDashboard(id: string) {
  return useQuery({ ...dashboardQuery(id), enabled: Boolean(id) });
}

// ---------------------------------------------------------------------------
// The front page
// ---------------------------------------------------------------------------

export type HomeState = components["schemas"]["HomeOut"];

export const homeQuery = queryOptions({
  queryKey: ["home"],
  queryFn: async () => unwrap(await api.GET("/api/home")),
});

export function useHome() {
  return useQuery(homeQuery);
}

/** Feature a dashboard on the front page, or `null` to stop. Admin. */
export function useSetFeaturedDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dashboardId: string | null) =>
      unwrap(
        await api.PUT("/api/home/featured-dashboard", { body: { dashboard_id: dashboardId } }),
      ),
    onSuccess: (state) => queryClient.setQueryData(homeQuery.queryKey, state),
  });
}

/**
 * Ask from the front page: a new conversation on `connection_id`, with its
 * first question already sent.
 *
 * Two requests, one operation — the chat page picks the run up from the
 * thread's `active_run`, exactly as it reattaches after a reload, so the
 * answer is streaming by the time the conversation opens. If the question
 * cannot be sent, the conversation is still there and still blank, and the
 * next one discards it (FR-4.8).
 */
export function useAskNewConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    // HomePage — under the ask bar, which keeps the question.
    meta: INLINE,
    mutationFn: async (body: { connection_id: string; text: string }) => {
      const thread = unwrap(
        await api.POST("/api/threads", {
          body: { connection_id: body.connection_id, title: "", replace_empty: true, model_ref: null },
        }),
      );
      const started = unwrap(
        await api.POST("/api/threads/{thread_id}/messages", {
          params: { path: { thread_id: thread.id } },
          body: { text: body.text, model_ref: null },
        }),
      );
      return { thread, run_id: started.run_id };
    },
    onSettled: () =>
      // The list only — see useCreateThread for why not every detail under it.
      queryClient.invalidateQueries({
        queryKey: ["threads"],
        predicate: (query) => typeof query.queryKey[1] === "object",
      }),
  });
}

/** A source's openers before any conversation exists (the home ask bar). */
export function useSourceLanding(connectionId: string) {
  return useQuery({
    queryKey: ["connections", connectionId, "landing"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/connections/{connection_id}/landing", {
          params: { path: { connection_id: connectionId } },
        }),
      ),
    enabled: Boolean(connectionId),
    staleTime: 5 * 60_000,
  });
}

/** Pin an answer as a report (PRD FR-7.1). */
export function useSaveReport() {
  const queryClient = useQueryClient();
  return useMutation({
    // PinMenus — inside the Save-as-report dialog.
    meta: INLINE,
    mutationFn: async (body: { message_id: string; title?: string }) =>
      unwrap(await api.POST("/api/reports", { body: { title: "", summary: "", ...body } })),
    onSuccess: (report) => {
      queryClient.setQueryData(reportQuery(report.id).queryKey, report);
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

/**
 * A report with nothing in it yet (PRD FR-7.1).
 *
 * The same endpoint `useSaveReport` posts to, with no `message_id`: one way to
 * create a report, whether it starts from an answer or from a blank page.
 */
export function useCreateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    // ReportsPage — inside the New report dialog.
    meta: INLINE,
    mutationFn: async (body: { title?: string; summary?: string }) =>
      unwrap(await api.POST("/api/reports", { body: { title: "", summary: "", ...body } })),
    onSuccess: (report) => {
      queryClient.setQueryData(reportQuery(report.id).queryKey, report);
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

/** A statement somebody typed, as a tile of a report (PRD FR-5.5, FR-7.1). */
export function useAddQueryToReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      reportId: string;
      connection_id: string;
      sql: string;
      title?: string;
      presentation?: Presentation | null;
    }) =>
      unwrap(
        await api.POST("/api/reports/{report_id}/tiles/from-query", {
          params: { path: { report_id: body.reportId } },
          body: {
            connection_id: body.connection_id,
            sql: body.sql,
            title: body.title ?? "",
            presentation: body.presentation ?? null,
          },
        }),
      ),
    onSuccess: (report) => {
      queryClient.setQueryData(reportQuery(report.id).queryKey, report);
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

/** "Add to…" from a chat artifact onto an existing report (PRD FR-7.1). */
export function useAddToReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reportId,
      messageId,
      artifactIndex,
    }: {
      reportId: string;
      messageId: string;
      artifactIndex?: number | null;
    }) =>
      unwrap(
        await api.POST("/api/reports/{report_id}/tiles/from-message", {
          params: { path: { report_id: reportId } },
          body: { message_id: messageId, artifact_index: artifactIndex ?? null },
        }),
      ),
    onSuccess: (report) => {
      queryClient.setQueryData(reportQuery(report.id).queryKey, report);
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

/** Rename a report (PRD FR-7.4). The endpoint has existed since M5. */
export function useUpdateReport(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // `EditableTitle` keeps the editor open on failure and the toast carries
    // the reason, so this stays on the default surface (backlog item 11).
    mutationFn: async (body: { title?: string; summary?: string }) =>
      unwrap(
        await api.PATCH("/api/reports/{report_id}", {
          params: { path: { report_id: id } },
          body,
        }),
      ),
    onSuccess: (report) => {
      queryClient.setQueryData(reportQuery(report.id).queryKey, report);
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

/** Rename a dashboard (PRD FR-7.3). */
export function useUpdateDashboard(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name?: string; description?: string }) =>
      unwrap(
        await api.PATCH("/api/dashboards/{dashboard_id}", {
          params: { path: { dashboard_id: id } },
          body,
        }),
      ),
    onSuccess: (dashboard) => {
      queryClient.setQueryData(dashboardQuery(dashboard.id).queryKey, dashboard);
      void queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/reports/{report_id}", {
          params: { path: { report_id: id } },
        }),
      ),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ["reports", id] });
      // Prefix, not exact: the list is keyed ["reports", { search }].
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useRenameReport(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { title?: string | null }) =>
      unwrap(
        await api.PATCH("/api/reports/{report_id}", {
          params: { path: { report_id: id } },
          body,
        }),
      ),
    onSuccess: (report) => {
      queryClient.setQueryData(reportQuery(id).queryKey, report);
      void queryClient.invalidateQueries({ queryKey: ["reports"], exact: false });
    },
  });
}

/** Refresh-on-open, and the button beside the stamp (PRD FR-7.4). */
export function useRefreshReport(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/reports/{report_id}/refresh", {
          params: { path: { report_id: id } },
        }),
      ),
    onSuccess: (report) => queryClient.setQueryData(reportQuery(id).queryKey, report),
  });
}

export function useSetReportLayout(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tiles: TileFrame[]) =>
      unwrap(
        await api.PUT("/api/reports/{report_id}/layout", {
          params: { path: { report_id: id } },
          body: { tiles },
        }),
      ),
    onSuccess: (report) => queryClient.setQueryData(reportQuery(id).queryKey, report),
  });
}

export function useCreateDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; description?: string }) =>
      unwrap(await api.POST("/api/dashboards", { body: { description: "", ...body } })),
    onSuccess: (dashboard) => {
      queryClient.setQueryData(dashboardQuery(dashboard.id).queryKey, dashboard);
      void queryClient.invalidateQueries({ queryKey: ["dashboards"], exact: true });
    },
  });
}

export function useRenameDashboard(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name?: string | null; description?: string | null }) =>
      unwrap(
        await api.PATCH("/api/dashboards/{dashboard_id}", {
          params: { path: { dashboard_id: id } },
          body,
        }),
      ),
    onSuccess: (dashboard) => {
      queryClient.setQueryData(dashboardQuery(id).queryKey, dashboard);
      void queryClient.invalidateQueries({ queryKey: ["dashboards"], exact: true });
    },
  });
}

export function useDeleteDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/dashboards/{dashboard_id}", {
          params: { path: { dashboard_id: id } },
        }),
      ),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ["dashboards", id] });
      void queryClient.invalidateQueries({ queryKey: ["dashboards"], exact: true });
      // A featured dashboard that is deleted is un-featured server-side.
      void queryClient.invalidateQueries({ queryKey: homeQuery.queryKey });
    },
  });
}

/** Refresh All: the dedup happens server-side (PRD FR-7.3). */
export function useRefreshDashboard(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/dashboards/{dashboard_id}/refresh", {
          params: { path: { dashboard_id: id } },
        }),
      ),
    onSuccess: (dashboard) => queryClient.setQueryData(dashboardQuery(id).queryKey, dashboard),
  });
}

export function useSetDashboardLayout(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tiles: TileFrame[]) =>
      unwrap(
        await api.PUT("/api/dashboards/{dashboard_id}/layout", {
          params: { path: { dashboard_id: id } },
          body: { tiles },
        }),
      ),
    onSuccess: (dashboard) => queryClient.setQueryData(dashboardQuery(id).queryKey, dashboard),
  });
}

/** "Add to…" from a chat artifact (PRD FR-7.3). */
export function useAddToDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dashboardId,
      messageId,
      artifactIndex,
    }: {
      dashboardId: string;
      messageId: string;
      artifactIndex?: number | null;
    }) =>
      unwrap(
        await api.POST("/api/dashboards/{dashboard_id}/tiles/from-message", {
          params: { path: { dashboard_id: dashboardId } },
          body: { message_id: messageId, artifact_index: artifactIndex ?? null },
        }),
      ),
    onSuccess: (dashboard) => {
      queryClient.setQueryData(dashboardQuery(dashboard.id).queryKey, dashboard);
      void queryClient.invalidateQueries({ queryKey: ["dashboards"], exact: true });
    },
  });
}

/** The SQL workbench's "pin this" (PRD FR-5.5). */
export function useAddQueryToDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      dashboardId: string;
      connection_id: string;
      sql: string;
      title?: string;
      presentation?: Presentation | null;
    }) =>
      unwrap(
        await api.POST("/api/dashboards/{dashboard_id}/tiles/from-query", {
          params: { path: { dashboard_id: body.dashboardId } },
          body: {
            connection_id: body.connection_id,
            sql: body.sql,
            title: body.title ?? "",
            presentation: body.presentation ?? null,
          },
        }),
      ),
    onSuccess: async (dashboard) => {
      await queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboards", dashboard.id] });
    },
  });
}

export function useAddReportToDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ dashboardId, reportId }: { dashboardId: string; reportId: string }) =>
      unwrap(
        await api.POST("/api/dashboards/{dashboard_id}/tiles/from-report", {
          params: { path: { dashboard_id: dashboardId } },
          body: { report_id: reportId },
        }),
      ),
    onSuccess: (dashboard) => {
      queryClient.setQueryData(dashboardQuery(dashboard.id).queryKey, dashboard);
      void queryClient.invalidateQueries({ queryKey: ["dashboards"], exact: true });
    },
  });
}

/** Every tile write invalidates whichever surface owns it. */
function invalidateOwner(queryClient: ReturnType<typeof useQueryClient>, tile: Tile) {
  const key = tile.owner_kind === "report" ? "reports" : "dashboards";
  void queryClient.invalidateQueries({ queryKey: [key, tile.owner_id] });
  void queryClient.invalidateQueries({ queryKey: [key], exact: true });
}

export function useRefreshTile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tileId: string) =>
      unwrap(
        await api.POST("/api/tiles/{tile_id}/refresh", { params: { path: { tile_id: tileId } } }),
      ),
    onSuccess: (tile) => invalidateOwner(queryClient, tile),
  });
}

export function useUpdateTile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tileId,
      ...body
    }: {
      tileId: string;
      title?: string | null;
      col_span?: number | null;
      row_span?: number | null;
    }) =>
      unwrap(
        await api.PATCH("/api/tiles/{tile_id}", { params: { path: { tile_id: tileId } }, body }),
      ),
    onSuccess: (tile) => invalidateOwner(queryClient, tile),
  });
}

export function useDeleteTile(owner: { kind: string; id: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tileId: string) =>
      unwrapEmpty(
        await api.DELETE("/api/tiles/{tile_id}", {
          params: { path: { tile_id: tileId } },
        }),
      ),
    onSuccess: () => {
      const key = owner.kind === "report" ? "reports" : "dashboards";
      void queryClient.invalidateQueries({ queryKey: [key, owner.id] });
      void queryClient.invalidateQueries({ queryKey: [key], exact: true });
    },
  });
}

/** A tile owns its own spec, so this must not touch the message (PRD FR-7.2). */
export function useSwapTileKind() {
  const queryClient = useQueryClient();
  return useMutation({
    // ArtifactCard, via TileCard — same card, same refusal.
    meta: INLINE,
    mutationFn: async ({ tileId, type }: { tileId: string; type: string }) =>
      unwrap(
        await api.PATCH("/api/tiles/{tile_id}/artifact", {
          params: { path: { tile_id: tileId } },
          body: { type },
        }),
      ),
    onSuccess: (tile) => invalidateOwner(queryClient, tile),
  });
}

/** How a result is drawn (TILE-1): `table`, a chart kind or a map, plus its encodings. */
export type Presentation = components["schemas"]["Presentation"];

/**
 * "Add a tile"'s preview (TILE-1): the statement guarded, run and drawn,
 * nothing saved — so the dialog can offer the result's columns to draw with.
 */
export function usePreviewTile() {
  return useMutation({
    mutationFn: async (body: {
      connection_id: string;
      sql: string;
      title?: string;
      presentation?: Presentation | null;
    }) =>
      unwrap(
        await api.POST("/api/tiles/preview", {
          body: {
            connection_id: body.connection_id,
            sql: body.sql,
            title: body.title ?? "",
            presentation: body.presentation ?? null,
          },
        }),
      ),
  });
}

/** Redraw a tile from the rows it has (TILE-1). Nothing runs; `dryRun` previews. */
export function useSetTilePresentation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tileId,
      presentation,
      dryRun,
    }: {
      tileId: string;
      presentation: Presentation;
      dryRun?: boolean;
    }) =>
      unwrap(
        await api.PATCH("/api/tiles/{tile_id}/presentation", {
          params: { path: { tile_id: tileId } },
          body: { ...presentation, dry_run: Boolean(dryRun) },
        }),
      ),
    onSuccess: (tile, { dryRun }) => {
      if (!dryRun) invalidateOwner(queryClient, tile);
    },
  });
}

/** Edit SQL: guarded, run and re-rendered *before* it is saved (PRD FR-7.2). */
export function useSetTileSql() {
  const queryClient = useQueryClient();
  return useMutation({
    // TileSqlDialog — a guard refusal is rendered with its code.
    meta: INLINE,
    mutationFn: async ({
      tileId,
      sql,
      dryRun,
    }: {
      tileId: string;
      sql: string;
      dryRun?: boolean;
    }) =>
      unwrap(
        await api.PUT("/api/tiles/{tile_id}/sql", {
          params: { path: { tile_id: tileId } },
          body: { sql, dry_run: Boolean(dryRun), connection_id: null },
        }),
      ),
    onSuccess: (result) => {
      if (result.saved) invalidateOwner(queryClient, result.tile);
    },
  });
}

/** Where a tile's CSV lives (PRD FR-6.6). A link, not a fetch. */
export function tileCsvUrl(tileId: string): string {
  return `/api/tiles/${encodeURIComponent(tileId)}/export.csv`;
}

// --------------------------------------------------------------------------
// Snapshots, trends, Cause Lens, schedules and delivery (M6 — PRD FR-7.5 to FR-9)
// --------------------------------------------------------------------------

export type SnapshotSummary = components["schemas"]["SnapshotSummary"];
export type SnapshotDetail = components["schemas"]["SnapshotDetail"];
export type SnapshotTile = components["schemas"]["SnapshotTile"];
export type Trend = components["schemas"]["TrendOut"];
export type TrendSeries = components["schemas"]["TrendSeriesOut"];
export type TrendPoint = components["schemas"]["TrendPointOut"];
export type CauseLensAnswer = components["schemas"]["CauseLensAnswer"];
export type CauseLensRun = components["schemas"]["CauseLensRun"];
export type Schedule = components["schemas"]["ScheduleOut"];
export type ScheduleWrite = components["schemas"]["ScheduleWrite"];
export type SchedulePatch = components["schemas"]["SchedulePatch"];
export type ScheduleRun = components["schemas"]["ScheduleRunOut"];
export type SchedulerStatus = components["schemas"]["SchedulerStatus"];
export type Delivery = components["schemas"]["DeliveryOut"];
export type DeliveryResult = components["schemas"]["DeliveryResult"];
export type Firing = components["schemas"]["FiringOut"];

export function reportSnapshotsQuery(id: string) {
  return queryOptions({
    queryKey: ["reports", id, "snapshots"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/reports/{report_id}/snapshots", {
          params: { path: { report_id: id } },
        }),
      ),
  });
}

export function reportTrendQuery(id: string) {
  return queryOptions({
    queryKey: ["reports", id, "trend"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/reports/{report_id}/trend", { params: { path: { report_id: id } } }),
      ),
  });
}

export function useReportSnapshots(id: string, enabled = true) {
  return useQuery({ ...reportSnapshotsQuery(id), enabled: enabled && Boolean(id) });
}

/** Every numeric KPI across a report's snapshots (PRD FR-7.5). */
export function useReportTrend(id: string, enabled = true) {
  return useQuery({ ...reportTrendQuery(id), enabled: enabled && Boolean(id) });
}

export function useDashboardTrend(id: string, enabled = true) {
  return useQuery({
    queryKey: ["dashboards", id, "trend"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/dashboards/{dashboard_id}/trend", {
          params: { path: { dashboard_id: id } },
        }),
      ),
    enabled: enabled && Boolean(id),
  });
}

export function useSnapshot(id: string, enabled = true) {
  return useQuery({
    queryKey: ["snapshots", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/snapshots/{snapshot_id}", { params: { path: { snapshot_id: id } } }),
      ),
    enabled: enabled && Boolean(id),
  });
}

/**
 * Capture a snapshot now (PRD FR-7.5).
 *
 * `captured_at` exists because a trend across three snapshots cannot wait
 * three days to be looked at — it fakes the clock, never the data.
 */
export function useCaptureSnapshot(reportId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { refresh?: boolean; captured_at?: string } = {}) =>
      unwrap(
        await api.POST("/api/reports/{report_id}/snapshots", {
          params: { path: { report_id: reportId } },
          body: { refresh: false, ...body },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports", reportId], exact: false });
    },
  });
}

export function useDeleteSnapshot(reportId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/snapshots/{snapshot_id}", {
          params: { path: { snapshot_id: id } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports", reportId], exact: false });
    },
  });
}

/** The last Cause Lens answer for a report, if one was ever asked for. */
export function useCauseLens(reportId: string, enabled = true) {
  return useQuery({
    queryKey: ["reports", reportId, "cause-lens"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/reports/{report_id}/cause-lens", {
          params: { path: { report_id: reportId } },
        }),
      ),
    enabled: enabled && Boolean(reportId),
  });
}

/**
 * Start a Cause Lens run (PRD FR-7.6).
 *
 * Returns a run id, exactly as sending a chat message does, so the trace
 * streams over the SSE hook the workspace already has.
 */
export function useRunCauseLens(reportId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { model_ref?: string | null } = {}) =>
      unwrap(
        await api.POST("/api/reports/{report_id}/cause-lens", {
          params: { path: { report_id: reportId } },
          body: { model_ref: null, ...body },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["reports", reportId, "cause-lens"],
      });
    },
  });
}

export const schedulesQuery = queryOptions({
  queryKey: ["schedules"],
  queryFn: async () => unwrap(await api.GET("/api/schedules")),
});

export const schedulerStatusQuery = queryOptions({
  queryKey: ["schedules", "status"],
  queryFn: async () => unwrap(await api.GET("/api/schedules/status")),
  refetchInterval: 30_000,
});

export const schedulePresetsQuery = queryOptions({
  queryKey: ["schedules", "presets"],
  queryFn: async () => unwrap(await api.GET("/api/schedules/presets")),
  staleTime: 300_000,
});

export function useSchedules() {
  return useQuery(schedulesQuery);
}

export function useSchedulerStatus() {
  return useQuery(schedulerStatusQuery);
}

export function useSchedulePresets() {
  return useQuery(schedulePresetsQuery);
}

export function useScheduleRuns(id: string, enabled = true) {
  return useQuery({
    queryKey: ["schedules", id, "runs"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/schedules/{schedule_id}/runs", {
          params: { path: { schedule_id: id } },
        }),
      ),
    enabled: enabled && Boolean(id),
  });
}

function invalidateSchedules(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["schedules"], exact: false });
  void queryClient.invalidateQueries({ queryKey: ["deliveries"], exact: false });
  // A schedule owns `auto_refresh` on its report, so the report list is stale too.
  void queryClient.invalidateQueries({ queryKey: ["reports"], exact: false });
  void queryClient.invalidateQueries({ queryKey: ["dashboards"], exact: false });
}

export function useCreateSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    // ScheduleDialog — in the dialog footer.
    meta: INLINE,
    mutationFn: async (body: ScheduleWrite) => unwrap(await api.POST("/api/schedules", { body })),
    onSuccess: () => invalidateSchedules(queryClient),
  });
}

export function useUpdateSchedule(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // ScheduleDialog — in the dialog footer.
    meta: INLINE,
    mutationFn: async (body: SchedulePatch) =>
      unwrap(
        await api.PATCH("/api/schedules/{schedule_id}", {
          params: { path: { schedule_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidateSchedules(queryClient),
  });
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/schedules/{schedule_id}", {
          params: { path: { schedule_id: id } },
        }),
      ),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ["schedules", id] });
      invalidateSchedules(queryClient);
    },
  });
}

/** Run the whole pipeline now; cannot consume the real window (PRD FR-8.3). */
export function useRunSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.POST("/api/schedules/{schedule_id}/run", {
          params: { path: { schedule_id: id } },
        }),
      ),
    onSuccess: () => invalidateSchedules(queryClient),
  });
}

/** FR-8.2's test-send: the email path, with the values as they stand. */
export function useTestSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.POST("/api/schedules/{schedule_id}/test", {
          params: { path: { schedule_id: id } },
        }),
      ),
    onSuccess: () => invalidateSchedules(queryClient),
  });
}

export function useDeliveries(
  filters: { schedule_id?: string; status?: string } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ["deliveries", filters],
    queryFn: async () =>
      unwrap(await api.GET("/api/deliveries", { params: { query: filters } })),
    refetchInterval: 60_000,
    enabled,
  });
}


// ---------------------------------------------------------------------------
// Health check (PRD FR-1.8)
// ---------------------------------------------------------------------------

export type HealthCheck = components["schemas"]["HealthCheckOut"];
export type HealthCheckStep = components["schemas"]["CheckStepOut"];
export type HealthProbe = components["schemas"]["ProbeOut"];

export function healthCheckQuery(connectionId: string) {
  return queryOptions({
    queryKey: ["health", connectionId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/connections/{connection_id}/health", {
          params: { path: { connection_id: connectionId } },
        }),
      ),
  });
}

/**
 * The latest verdict, polled while a check is running.
 *
 * Polled rather than streamed: a health check is four sequential agent runs
 * and can take minutes on a local model, and each graded step lands in
 * `checks` as soon as it finishes — so the page fills in without a second
 * streaming path to keep correct.
 */
export function useHealthCheck(connectionId: string, enabled = true) {
  return useQuery({
    ...healthCheckQuery(connectionId),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.status === "running" ? 2_000 : false,
  });
}

export function useHealthCheckHistory(connectionId: string, enabled = true) {
  return useQuery({
    queryKey: ["health", connectionId, "history"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/connections/{connection_id}/health/history", {
          params: { path: { connection_id: connectionId } },
        }),
      ),
    enabled,
  });
}

export function useHealthProbes(enabled = false) {
  return useQuery({
    queryKey: ["health", "probes"],
    queryFn: async () => unwrap(await api.GET("/api/health/probes")),
    staleTime: Infinity,
    enabled,
  });
}

export function useRunHealthCheck(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // HealthCheckPanel — beside the graded results.
    meta: INLINE,
    mutationFn: async (modelRef?: string) =>
      unwrap(
        await api.POST("/api/connections/{connection_id}/health", {
          params: { path: { connection_id: connectionId } },
          body: { model_ref: modelRef || null },
        }),
      ),
    onSuccess: async (check) => {
      queryClient.setQueryData(healthCheckQuery(connectionId).queryKey, check);
      await queryClient.invalidateQueries({ queryKey: ["health", connectionId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Business Memory (PRD FR-2.3)
// ---------------------------------------------------------------------------

export type BusinessMemoryDraft = components["schemas"]["DraftOut"];

export function useBusinessMemory(
  connectionId: string,
  mode: "append" | "replace",
  enabled = true,
) {
  return useQuery({
    queryKey: ["memory", connectionId, mode],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/connections/{connection_id}/memory", {
          params: { path: { connection_id: connectionId }, query: { mode } },
        }),
      ),
    enabled,
  });
}

export function useDraftBusinessMemory(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // BusinessMemoryPanel — above the draft.
    meta: INLINE,
    mutationFn: async (modelRef?: string) =>
      unwrap(
        await api.POST("/api/connections/{connection_id}/memory/draft", {
          params: { path: { connection_id: connectionId } },
          body: { model_ref: modelRef || null },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["memory", connectionId] }),
  });
}

/** The review step FR-2.3 asks for: nothing is written until this is called. */
export function useApplyBusinessMemory(connectionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // BusinessMemoryPanel — above the draft.
    meta: INLINE,
    mutationFn: async (body: { mode: "append" | "replace"; text?: string }) =>
      unwrap(
        await api.POST("/api/connections/{connection_id}/memory/apply", {
          params: { path: { connection_id: connectionId } },
          body: { mode: body.mode, text: body.text ?? null },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["memory", connectionId] });
      await queryClient.invalidateQueries({ queryKey: ["connections", connectionId] });
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Audit browser (PRD FR-10.3, NFR-6)
// ---------------------------------------------------------------------------

export type QueryAuditEntry = components["schemas"]["QueryAuditEntry"];
export type AuditEventEntry = components["schemas"]["AuditEventEntry"];
export type AuditSummary = components["schemas"]["AuditSummary"];
export type AnswerReconstruction = components["schemas"]["AnswerReconstruction"];

export interface QueryAuditFilters {
  connection_id?: string;
  outcome?: "ok" | "blocked" | "error";
  origin?: string;
  /** `staged` — only cross-source joins; `source` — only statements that reached a source. */
  scope?: "source" | "staged";
  search?: string;
  limit?: number;
  offset?: number;
}

export function useQueryAudit(filters: QueryAuditFilters) {
  return useQuery({
    queryKey: ["audit", "queries", filters],
    queryFn: async () =>
      unwrap(await api.GET("/api/audit/queries", { params: { query: filters } })),
  });
}

export function useAuditSummary(days = 30) {
  return useQuery({
    queryKey: ["audit", "summary", { days }],
    queryFn: async () =>
      unwrap(await api.GET("/api/audit/summary", { params: { query: { days } } })),
  });
}

export function useAuditEvents(
  filters: { category?: string; subject_kind?: string; subject_id?: string; limit?: number } = {},
) {
  return useQuery({
    queryKey: ["audit", "events", filters],
    queryFn: async () =>
      unwrap(await api.GET("/api/audit/events", { params: { query: filters } })),
  });
}

/** NFR-6: reconstruct exactly how one answer was produced. */
export function useAnswerReconstruction(messageId: string | null) {
  return useQuery({
    queryKey: ["audit", "answers", messageId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/audit/answers/{message_id}", {
          params: { path: { message_id: messageId as string } },
        }),
      ),
    enabled: Boolean(messageId),
  });
}

// ---------------------------------------------------------------------------
// Theming, limits and retention (PRD FR-11.1, FR-10.4, FR-10.6)
// ---------------------------------------------------------------------------

export type ThemeAdminState = components["schemas"]["ThemeAdminState"];
export type BrandingAsset = components["schemas"]["BrandingAsset"];
export type BrandOverlay = components["schemas"]["BrandOverlay"];
export type LimitsState = components["schemas"]["LimitsState"];
export type RetentionState = components["schemas"]["RetentionState"];
export type RetentionPolicy = components["schemas"]["RetentionPolicyOut"];
export type RetentionRun = components["schemas"]["RetentionRunOut"];

export const brandOverlayQuery = queryOptions({
  queryKey: ["admin", "theme"],
  queryFn: async () => unwrap(await api.GET("/api/admin/theme")),
});

export type ThemeWarning = components["schemas"]["ThemeWarning"];
export type ThemePreview = components["schemas"]["ThemePreview"];

/**
 * What an unsaved edit would produce: the derived ramp, the linked platform
 * tokens and the warnings. A query over a POST because it writes nothing —
 * and because the alternative, deriving the ramp in the browser, is a second
 * implementation of "what does this accent imply" that would drift from the
 * one that ships.
 */
export function useBrandPreview(body: BrandOverlay, enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "theme", "preview", body],
    queryFn: async () => unwrap(await api.POST("/api/admin/theme/preview", { body })),
    enabled,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
}

export function useBrandOverlay(enabled = true) {
  return useQuery({ ...brandOverlayQuery, enabled });
}

/**
 * A rebrand reaches the app shell, the unlock page, the PDFs and the emails,
 * because all four read the same theme document — so every theme query is
 * invalidated, *and* the live stylesheet is swapped. The second half was only
 * ever promised by this comment: colours and fonts stayed as they were until
 * a full reload, which is what made a rebrand look like it had done nothing.
 */
function invalidateTheme(queryClient: QueryClient) {
  // Not awaited: a save is done when the server says so, and a slow webfont
  // host must not hold the Apply button in its loading state.
  void reloadThemeStylesheet();
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["admin", "theme"] }),
    queryClient.invalidateQueries({ queryKey: themeQuery.queryKey }),
  ]);
}

export function useUpdateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    // ThemingPage — under the Save button.
    meta: INLINE,
    mutationFn: async (body: BrandOverlay) =>
      unwrap(await api.PUT("/api/admin/theme", { body })),
    onSuccess: async (state) => {
      queryClient.setQueryData(brandOverlayQuery.queryKey, state);
      queryClient.setQueryData(themeQuery.queryKey, state.document);
      await invalidateTheme(queryClient);
    },
  });
}

export function useResetBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await api.DELETE("/api/admin/theme")),
    onSuccess: async (state) => {
      queryClient.setQueryData(brandOverlayQuery.queryKey, state);
      queryClient.setQueryData(themeQuery.queryKey, state.document);
      await invalidateTheme(queryClient);
    },
  });
}

/**
 * Delete an uploaded logo or favicon from the data volume.
 *
 * Returns the whole admin state because the server may have cleared a brand
 * key that pointed at the removed file — so the page has to re-render from the
 * server's answer, not from what it thought the overlay was.
 */
export function useDeleteBrandingAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    // ThemingPage — beside the file it could not delete.
    meta: INLINE,
    mutationFn: async (slot: string) =>
      unwrap(
        await api.DELETE("/api/admin/theme/assets/{slot}", {
          params: { path: { slot } },
        }),
      ),
    onSuccess: async (state) => {
      queryClient.setQueryData(brandOverlayQuery.queryKey, state);
      queryClient.setQueryData(themeQuery.queryKey, state.document);
      await invalidateTheme(queryClient);
    },
  });
}

export function useLimits() {
  return useQuery({
    queryKey: ["admin", "limits"],
    queryFn: async () => unwrap(await api.GET("/api/admin/limits")),
    refetchInterval: 15_000,
  });
}

export function useUpdateLimits() {
  const queryClient = useQueryClient();
  return useMutation({
    // OperationsPage — under the limits form.
    meta: INLINE,
    mutationFn: async (body: { max_concurrent_runs?: number; chat_rate_limit_messages?: number }) =>
      unwrap(await api.PATCH("/api/admin/limits", { body })),
    onSuccess: async (state) => {
      queryClient.setQueryData(["admin", "limits"], state);
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export function useRetention() {
  return useQuery({
    queryKey: ["admin", "retention"],
    queryFn: async () => unwrap(await api.GET("/api/admin/retention")),
  });
}

export function useRunRetention() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/admin/retention/run")),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "retention"] });
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
      await queryClient.invalidateQueries({ queryKey: ["training"] });
      await queryClient.invalidateQueries({ queryKey: ["usage"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Training log (PRD FR-4.10)
// ---------------------------------------------------------------------------

export type TrainingExample = components["schemas"]["TrainingExampleOut"];
export type TrainingPage = components["schemas"]["TrainingPage"];

export interface TrainingFilters {
  connection_id?: string;
  model_ref?: string;
  rated_only?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export function useTrainingLog(filters: TrainingFilters) {
  return useQuery({
    queryKey: ["training", filters],
    queryFn: async () => unwrap(await api.GET("/api/training", { params: { query: filters } })),
  });
}

/** The export URL, so the browser downloads it rather than the client buffering it. */
export function trainingExportUrl(filters: TrainingFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== "" && value !== false) params.set(key, String(value));
  }
  const query = params.toString();
  return `/api/training/export${query ? `?${query}` : ""}`;
}

// ---------------------------------------------------------------------------
// The core prompt: editing, pinning and rollback (PRD FR-2.2)
// ---------------------------------------------------------------------------

export type PromptState = components["schemas"]["PromptState"];
export type PromptVariantState = components["schemas"]["VariantState"];
export type PromptRevision = components["schemas"]["RevisionOut"];
export type PromptRevisionDetail = components["schemas"]["RevisionDetail"];
export type PromptLayerIn = components["schemas"]["PromptLayerIn"];
export type PromptPreview = components["schemas"]["PromptPreview"];
export type PromptProblem = components["schemas"]["ProblemOut"];

export const promptStateQuery = queryOptions({
  queryKey: ["admin", "prompts"],
  queryFn: async () => unwrap(await api.GET("/api/admin/prompts")),
});

export function usePromptState() {
  return useQuery(promptStateQuery);
}

export function usePromptHistory() {
  return useQuery({
    queryKey: ["admin", "prompts", "history"],
    queryFn: async () => unwrap(await api.GET("/api/admin/prompts/history")),
  });
}

export function usePromptRevision(revision: number | null) {
  return useQuery({
    queryKey: ["admin", "prompts", "revision", revision],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/admin/prompts/{revision}", {
          params: { path: { revision: revision as number } },
        }),
      ),
    enabled: revision !== null,
  });
}

/**
 * The server's validator, reachable while the author is still typing.
 *
 * Deliberately not a client-side copy of it: two implementations of "which
 * placeholders are required" would drift, and the one that decided whether an
 * answer could be produced would be the one nobody was looking at.
 */
export function usePromptPreview() {
  return useMutation({
    // PromptsPage — a debounced validator, not an action a reader took; a failed preview surfaces as the save's own refusal.
    meta: INLINE,
    mutationFn: async (input: { body: PromptLayerIn; connectionId?: string }) =>
      unwrap(
        await api.POST("/api/admin/prompts/preview", {
          body: input.body,
          params: { query: input.connectionId ? { connection_id: input.connectionId } : {} },
        }),
      ),
  });
}

/** Every prompt write returns the whole state, so the page re-renders from the
 * server's answer rather than from what it thought it had saved. */
function promptWriteOptions(queryClient: QueryClient) {
  return {
    onSuccess: async (state: PromptState) => {
      queryClient.setQueryData(promptStateQuery.queryKey, state);
      await queryClient.invalidateQueries({ queryKey: ["admin", "prompts"] });
    },
  };
}

export function useSavePrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    // PromptsPage — beside the field-level problems it carries.
    meta: INLINE,
    mutationFn: async (body: PromptLayerIn) =>
      unwrap(await api.POST("/api/admin/prompts", { body })),
    ...promptWriteOptions(queryClient),
  });
}

/**
 * Activate a stored revision — with the eval run it rests on, when there is
 * one (EVAL-3). The run id is recorded in the activation's audit row; without
 * one the row says so. Offer, never block.
 */
export function useActivatePromptRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ revision, evalRunId }: { revision: number; evalRunId?: string | null }) =>
      unwrap(
        await api.POST("/api/admin/prompts/{revision}/activate", {
          params: { path: { revision } },
          body: evalRunId ? { eval_run_id: evalRunId } : null,
        }),
      ),
    ...promptWriteOptions(queryClient),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["evals", "status"] });
    },
  });
}

export function useResetPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await api.DELETE("/api/admin/prompts")),
    ...promptWriteOptions(queryClient),
  });
}

// ---------------------------------------------------------------------------
// Share links (ACC-1 — the Viewer tier)
// ---------------------------------------------------------------------------

export type ShareLink = components["schemas"]["ShareOut"];
export type CreatedShare = components["schemas"]["CreatedShareOut"];
export type ShareSubjectKind = "report" | "dashboard";

function sharesKey(kind: ShareSubjectKind, id: string) {
  return [`${kind}s`, id, "shares"] as const;
}

export function useShares(kind: ShareSubjectKind, id: string, enabled = true) {
  return useQuery({
    queryKey: sharesKey(kind, id),
    queryFn: async () =>
      kind === "report"
        ? unwrap(
            await api.GET("/api/reports/{report_id}/shares", {
              params: { path: { report_id: id } },
            }),
          )
        : unwrap(
            await api.GET("/api/dashboards/{dashboard_id}/shares", {
              params: { path: { dashboard_id: id } },
            }),
          ),
    enabled: enabled && Boolean(id),
  });
}

/**
 * The URL in the response is shown once and never again — only a hash of its
 * secret is stored — so the dialog that called this owns showing it, and its
 * failure too.
 */
export function useCreateShare(kind: ShareSubjectKind, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: INLINE,
    mutationFn: async (body: {
      label: string;
      expires_in_days: number | null;
      embed_origins?: string[];
    }) =>
      kind === "report"
        ? unwrap(
            await api.POST("/api/reports/{report_id}/shares", {
              params: { path: { report_id: id } },
              body,
            }),
          )
        : unwrap(
            await api.POST("/api/dashboards/{dashboard_id}/shares", {
              params: { path: { dashboard_id: id } },
              body,
            }),
          ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sharesKey(kind, id) });
    },
  });
}

export function useRevokeShare(kind: ShareSubjectKind, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (shareId: string) =>
      unwrapEmpty(
        await api.DELETE("/api/shares/{share_id}", {
          params: { path: { share_id: shareId } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sharesKey(kind, id) });
    },
  });
}

/** Name the sites that may frame a share (HTML-2). Empty stops embedding. */
export function useUpdateShareEmbed(kind: ShareSubjectKind, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // ShareDialog — under the share's site list.
    meta: INLINE,
    mutationFn: async ({ shareId, origins }: { shareId: string; origins: string[] }) =>
      unwrap(
        await api.PATCH("/api/shares/{share_id}", {
          params: { path: { share_id: shareId } },
          body: { embed_origins: origins },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sharesKey(kind, id) });
    },
  });
}

/** The `<iframe>` a host page pastes. Sized by the host; this is a start. */
export function embedSnippet(embedUrl: string, title: string): string {
  const escaped = title.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  return `<iframe src="${embedUrl}" title="${escaped}" width="100%" height="900" style="border:0" loading="lazy" referrerpolicy="no-referrer"></iframe>`;
}

// ---------------------------------------------------------------------------
// Named accounts (ACC-3) — Admin only
// ---------------------------------------------------------------------------

export type UserAccount = components["schemas"]["UserOut"];

const usersKey = ["admin", "users"] as const;

export function useUsers() {
  return useQuery({
    queryKey: usersKey,
    queryFn: async () => unwrap(await api.GET("/api/users")),
  });
}

/** The Users page's form renders its own refusal — the sentence says which rule. */
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: INLINE,
    mutationFn: async (body: {
      username: string;
      display_name: string;
      role: "admin" | "analyst";
      password: string;
    }) => unwrap(await api.POST("/api/users", { body })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: usersKey });
      // The unlock page offers to sign in by name once anybody can.
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      display_name?: string;
      role?: "admin" | "analyst";
      disabled?: boolean;
    }) =>
      unwrap(
        await api.PATCH("/api/users/{user_id}", { params: { path: { user_id: id } }, body }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: usersKey });
    },
  });
}

/** The password dialog renders its own refusal, beside the field it is about. */
export function useSetUserPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: INLINE,
    mutationFn: async ({ id, password }: { id: string; password: string }) =>
      unwrap(
        await api.POST("/api/users/{user_id}/password", {
          params: { path: { user_id: id } },
          body: { password },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: usersKey });
    },
  });
}

// ---------------------------------------------------------------------------
// Operator notices (ALERT-1) — Admin only
// ---------------------------------------------------------------------------

export type NotificationSettings = components["schemas"]["NotificationSettings"];
export type OperatorNotice = components["schemas"]["NoticeOut"];

const notificationsKey = ["admin", "notifications"] as const;

export function useNotificationSettings() {
  return useQuery({
    queryKey: notificationsKey,
    queryFn: async () => unwrap(await api.GET("/api/admin/notifications")),
  });
}

/** The Operations card renders its own refusal, beside the field it is about. */
export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: INLINE,
    mutationFn: async (body: { emails: string[]; notify_recovery: boolean }) =>
      unwrap(await api.PUT("/api/admin/notifications", { body })),
    onSuccess: (state) => queryClient.setQueryData(notificationsKey, state),
  });
}

export function useResetNotificationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrapEmpty(await api.DELETE("/api/admin/notifications")),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKey });
    },
  });
}

export function useNotices(enabled = true) {
  return useQuery({
    queryKey: ["admin", "notices"],
    queryFn: async () => unwrap(await api.GET("/api/notices", { params: { query: { limit: 20 } } })),
    enabled,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Threshold alerts (ALERT-2)
// ---------------------------------------------------------------------------

export type AlertRule = components["schemas"]["AlertRuleOut"];
export type AlertEvent = components["schemas"]["AlertEventOut"];
export type AlertRuleWrite = components["schemas"]["AlertRuleWrite"];

export function useAlerts(subject?: { kind: "report" | "dashboard"; id: string }, enabled = true) {
  return useQuery({
    queryKey: subject ? ["alerts", subject.kind, subject.id] : ["alerts"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/alerts", {
          params: {
            query: subject ? { subject_kind: subject.kind, subject_id: subject.id } : {},
          },
        }),
      ),
    enabled,
  });
}

export function useAlertEvents(ruleId: string, enabled = true) {
  return useQuery({
    queryKey: ["alerts", "events", ruleId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/alerts/{rule_id}/events", {
          params: { path: { rule_id: ruleId }, query: { limit: 10 } },
        }),
      ),
    enabled: enabled && Boolean(ruleId),
  });
}

/** The alert dialog renders its own refusal, beside the form it is about. */
export function useSaveAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: INLINE,
    mutationFn: async ({
      id,
      subject,
      body,
    }: {
      id?: string;
      subject: { kind: "report" | "dashboard"; id: string };
      body: AlertRuleWrite;
    }) =>
      id
        ? unwrap(await api.PUT("/api/alerts/{rule_id}", { params: { path: { rule_id: id } }, body }))
        : unwrap(
            await api.POST("/api/alerts", {
              body: { ...body, subject_kind: subject.kind, subject_id: subject.id },
            }),
          ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}

export function useDeleteAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(await api.DELETE("/api/alerts/{rule_id}", { params: { path: { rule_id: id } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}

export function useAlertEvent(eventId: string | null) {
  return useQuery({
    queryKey: ["alerts", "event", eventId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/alerts/events/{event_id}", {
          params: { path: { event_id: eventId ?? "" } },
        }),
      ),
    enabled: Boolean(eventId),
  });
}

/**
 * "Explain this breach" (ALERT-3) — spends model tokens, so it is only ever
 * the confirmed half of a dialog; following an alert's link reads the event
 * and nothing more. Failure is announced by the mutation cache, and the
 * confirm stays open for a retry.
 */
export function useExplainAlertEvent(reportId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string) =>
      unwrap(
        await api.POST("/api/alerts/events/{event_id}/explain", {
          params: { path: { event_id: eventId } },
          body: { model_ref: null },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["reports", reportId, "cause-lens"] });
    },
  });
}

// ---------------------------------------------------------------------------
// The eval harness (EVAL-1) — Admin only
// ---------------------------------------------------------------------------

export type EvalSuite = components["schemas"]["EvalSuiteOut"];
export type EvalCase = components["schemas"]["EvalCaseOut"];
export type EvalCaseWrite = components["schemas"]["EvalCaseIn"];
export type EvalRun = components["schemas"]["EvalRunOut"];
export type EvalResult = components["schemas"]["EvalResultOut"];
export type EvalGrader = components["schemas"]["EvalGraderOut"];

export function useEvalGraders() {
  return useQuery({
    queryKey: ["evals", "graders"],
    queryFn: async () => unwrap(await api.GET("/api/evals/graders")),
    staleTime: 300_000,
  });
}

/** Admin-only on the server; pass `enabled: false` where an Analyst may be looking. */
export function useEvalSuites({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["evals", "suites"],
    queryFn: async () => unwrap(await api.GET("/api/evals/suites")),
    enabled,
  });
}

export function useEvalRuns(suiteId: string | null) {
  return useQuery({
    queryKey: ["evals", "runs", suiteId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/evals/suites/{suite_id}/runs", {
          params: { path: { suite_id: suiteId ?? "" } },
        }),
      ),
    enabled: Boolean(suiteId),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((run) => run.status === "running") ? 2_000 : false,
  });
}

/** Polled while running: the ladder fills in from the first poll (backlog item 8). */
export function useEvalRun(runId: string | null) {
  return useQuery({
    queryKey: ["evals", "run", runId],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/evals/runs/{run_id}", { params: { path: { run_id: runId ?? "" } } }),
      ),
    enabled: Boolean(runId),
    refetchInterval: (query) => (query.state.data?.status === "running" ? 1_500 : false),
  });
}

function invalidateEvals(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ["evals"] });
}

export function useCreateStarterSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    /** One source: the five-case starter. Several, primary first: the
     * multi-source starter (MSE-2). */
    mutationFn: async (connectionIds: string[]) =>
      unwrap(
        await api.POST("/api/evals/suites/starter", { body: { connection_ids: connectionIds } }),
      ),
    onSuccess: () => invalidateEvals(queryClient),
  });
}

export function useCreateEvalSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; connection_ids: string[] }) =>
      unwrap(await api.POST("/api/evals/suites", { body: { ...body, description: "" } })),
    onSuccess: () => invalidateEvals(queryClient),
  });
}

export function useDeleteEvalSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/evals/suites/{suite_id}", { params: { path: { suite_id: id } } }),
      ),
    onSuccess: () => invalidateEvals(queryClient),
  });
}

/** The case editor renders its own refusal, beside the form. */
export function useSaveEvalCase(suiteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    meta: INLINE,
    mutationFn: async ({ id, body }: { id?: string; body: EvalCaseWrite }) =>
      id
        ? unwrap(await api.PUT("/api/evals/cases/{case_id}", { params: { path: { case_id: id } }, body }))
        : unwrap(
            await api.POST("/api/evals/suites/{suite_id}/cases", {
              params: { path: { suite_id: suiteId } },
              body,
            }),
          ),
    onSuccess: () => invalidateEvals(queryClient),
  });
}

export function useDeleteEvalCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrapEmpty(
        await api.DELETE("/api/evals/cases/{case_id}", { params: { path: { case_id: id } } }),
      ),
    onSuccess: () => invalidateEvals(queryClient),
  });
}

export function usePromoteToEvalCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ suiteId, messageId }: { suiteId: string; messageId: string }) =>
      unwrap(
        await api.POST("/api/evals/suites/{suite_id}/cases/from-message", {
          params: { path: { suite_id: suiteId } },
          body: { message_id: messageId },
        }),
      ),
    onSuccess: () => invalidateEvals(queryClient),
  });
}

/** A new suite on the sources the answer read, the answer as its first case (MSE-3). */
export function usePromoteToNewEvalSuite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string) =>
      unwrap(
        await api.POST("/api/evals/suites/from-message", {
          body: { message_id: messageId },
        }),
      ),
    onSuccess: () => invalidateEvals(queryClient),
  });
}

export function useStartEvalRun(suiteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (modelRef: string | null) =>
      unwrap(
        await api.POST("/api/evals/suites/{suite_id}/runs", {
          params: { path: { suite_id: suiteId } },
          body: { model_ref: modelRef },
        }),
      ),
    onSuccess: () => invalidateEvals(queryClient),
  });
}

export function useStopEvalRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.POST("/api/evals/runs/{run_id}/stop", { params: { path: { run_id: id } } })),
    onSuccess: () => invalidateEvals(queryClient),
  });
}

export type EvalComparison = components["schemas"]["EvalComparisonOut"];

export function useCompareEvalRuns(a: string | null, b: string | null) {
  return useQuery({
    queryKey: ["evals", "compare", a, b],
    queryFn: async () =>
      unwrap(await api.GET("/api/evals/compare", { params: { query: { a: a ?? "", b: b ?? "" } } })),
    enabled: Boolean(a && b && a !== b),
  });
}

/** Writes a labelled measurement onto the model's catalog row (EVAL-2). */
export function usePublishEvalRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string) =>
      unwrap(
        await api.POST("/api/evals/runs/{run_id}/publish", { params: { path: { run_id: runId } } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["models"] });
      void queryClient.invalidateQueries({ queryKey: ["providers"] });
    },
  });
}

export type EvalStatus = components["schemas"]["EvalStatusOut"];

export function useEvalStatus() {
  return useQuery({
    queryKey: ["evals", "status"],
    queryFn: async () => unwrap(await api.GET("/api/evals/status")),
    refetchInterval: 15_000,
  });
}

/** Measure a stored prompt revision before it is live (EVAL-3). */
export function useMeasurePromptRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      suiteId,
      revision,
      modelRef,
    }: {
      suiteId: string;
      revision: number | null;
      modelRef: string | null;
    }) =>
      unwrap(
        await api.POST("/api/evals/suites/{suite_id}/runs", {
          params: { path: { suite_id: suiteId } },
          body: { model_ref: modelRef, prompt_revision: revision },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["evals"] });
    },
  });
}

export function useSetEvalRegression(suiteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      enabled: boolean;
      weekday: number;
      hour: number;
      model_ref: string | null;
    }) =>
      unwrap(
        await api.PUT("/api/evals/suites/{suite_id}/regression", {
          params: { path: { suite_id: suiteId } },
          body,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["evals"] });
    },
  });
}

/**
 * The print route's reads (M8.2). The server shapes them for a printed page —
 * a long table cut to its print limit, with a note saying where the rest is —
 * so the PDF and the Markdown export follow one rule decided in one place.
 * Separate cache keys: the screen must never be handed the cut copy.
 */
export function usePrintedReport(id: string) {
  return useQuery({
    queryKey: ["reports", id, "print"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/reports/{report_id}", {
          params: { path: { report_id: id }, query: { print: true } },
        }),
      ),
    enabled: Boolean(id),
  });
}

export function usePrintedDashboard(id: string) {
  return useQuery({
    queryKey: ["dashboards", id, "print"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/dashboards/{dashboard_id}", {
          params: { path: { dashboard_id: id }, query: { print: true } },
        }),
      ),
    enabled: Boolean(id),
  });
}

export function usePrintedThread(id: string) {
  return useQuery({
    queryKey: ["threads", id, "print"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/threads/{thread_id}", {
          params: { path: { thread_id: id }, query: { print: true } },
        }),
      ),
    enabled: Boolean(id),
  });
}

/** Where an export is downloaded from — a plain link: the session cookie rides along. */
export const CONFIG_EXPORT_URL = "/api/admin/config/export";

/**
 * Plan or apply a configuration import (M8.4). The plan is the same server
 * path as the apply, so the preview is what happens. A refusal (not a Ming
 * export, a newer one) is the server's sentence, through the default toast.
 */
export function useImportConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { document: Record<string, unknown>; dry_run: boolean }) =>
      unwrap(await api.POST("/api/admin/config/import", { body })),
    onSuccess: async (result) => {
      // The brand travels in a configuration, and the stylesheet is not a query.
      if (!result.dry_run)
        await Promise.all([queryClient.invalidateQueries(), reloadThemeStylesheet()]);
    },
  });
}
